// The panels the issuing page is made of, as data.
//
// A panel is a *template*: the parts that never vary (which fact, which
// dimensions, which measures) live here; the parts that come from the request
// (the window, the bucket, the active filters) are supplied by
// `buildAggregateSpec`. Keeping the templates in this package rather than in the
// service means "is every panel valid against its fact?" is a pure test with no
// database, and a panel cannot drift from the vocabulary it is written in.
//
// Every panel returns *both* measures even though the UI shows one at a time, so
// a panel's payload is complete whichever measure the reader picks.
//
// That does not make the Units/Issues toggle free. `top_items` is ranked by the
// active measure, so the measure is necessarily part of the request, and
// changing it refetches — one round-trip covering all four panels, served from
// cache on the way back. The three unranked panels genuinely don't depend on it;
// only the ranked one does, and it is the reason the parameter exists at all.

import type { DimensionId } from "./dimensions";
import type { FactId } from "./facts";
import type { MeasureId } from "./measures";
import { TIME_DIMENSION } from "./spec";
import type {
  AggregateSpec,
  AggregateWindow,
  BucketGranularity,
  DimensionFilter,
} from "./spec";

export const PANEL_IDS = ["totals", "trend", "by_type", "top_items"] as const;

export type PanelId = (typeof PANEL_IDS)[number];

/** How many items the ranked panel returns. */
export const TOP_ITEMS_LIMIT = 10;

export type PanelSpec = {
  readonly id: PanelId;
  readonly fact: FactId;
  readonly groupBy: readonly DimensionId[];
  readonly measures: readonly MeasureId[];
  /**
   * Keep only the top N groups, ranked by whichever measure the user is looking
   * at — so the ranked list re-sorts with the Units/Issues toggle rather than
   * being fixed to one of them. Absent means "every group, stable key order".
   *
   * A single number rather than a `{ ordering, limit }` pair on purpose: as two
   * optional fields, a panel could declare a limit with no ranking and get an
   * arbitrary slice presented as a top-10.
   */
  readonly topN?: number;
};

// Annotated rather than `as const satisfies`: the panels differ in which
// optional fields they carry, and under `as const` the union of their literal
// types has no common `topN` to read. `Record<PanelId, …>` still makes a missing
// or misspelled panel a compile error, which is the property that matters.
export const PANELS: Record<PanelId, PanelSpec> = {
  // Job 1's headline: one row, no grouping.
  totals: {
    id: "totals",
    fact: "stock_issues",
    groupBy: [],
    measures: ["units", "lines"],
  },
  // Jobs 1 and 3: the time series, stacked by type.
  trend: {
    id: "trend",
    fact: "stock_issues",
    groupBy: [TIME_DIMENSION, "item_type"],
    measures: ["units", "lines"],
  },
  // Job 2.
  by_type: {
    id: "by_type",
    fact: "stock_issues",
    groupBy: ["item_type"],
    measures: ["units", "lines"],
  },
  // The discovery path into job 3 — this is how a user finds the medicine to
  // filter to.
  top_items: {
    id: "top_items",
    fact: "stock_issues",
    groupBy: ["item"],
    measures: ["units", "lines"],
    topN: TOP_ITEMS_LIMIT,
  },
};

/**
 * Panels that are also computed over the preceding equal-length window so the UI
 * can show a delta. A list rather than a flag on `PanelSpec`, because a boolean
 * opt is how behaviour variants start turning into a matrix of flags.
 */
export const COMPARISON_PANEL_IDS = ["totals"] as const satisfies readonly PanelId[];

export type ComparisonPanelId = (typeof COMPARISON_PANEL_IDS)[number];

export type PanelRequest = {
  readonly window: AggregateWindow;
  readonly bucket: BucketGranularity;
  readonly filters: readonly DimensionFilter[];
  /** The measure the user is currently looking at; decides ranking. */
  readonly activeMeasure: MeasureId;
};

/**
 * Combine a panel template with the request-scoped parts into a spec the
 * repository can run.
 *
 * `bucket` is attached only to panels that group by time, which is how the
 * "bucket iff time_bucket" invariant is upheld at the one place specs are built
 * rather than restated at each call site.
 */
export function buildAggregateSpec(panelId: PanelId, request: PanelRequest): AggregateSpec {
  const panel = PANELS[panelId];
  const groupsByTime = panel.groupBy.includes(TIME_DIMENSION);

  return {
    fact: panel.fact,
    window: request.window,
    ...(groupsByTime ? { bucket: request.bucket } : {}),
    groupBy: panel.groupBy,
    measures: panel.measures,
    filters: request.filters,
    ...(panel.topN === undefined
      ? {}
      : { topBy: { measure: request.activeMeasure, limit: panel.topN } }),
  };
}
